import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../context/I18nContext";
import TrustBadges from "../components/TrustBadges";

export default function Paywall() {
  const { user, refresh } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState("");
  const [testMode, setTestMode] = useState(false);
  const [autoRecovering, setAutoRecovering] = useState(true);

  // The instant a user lands on the paywall while logged in, ask Stripe directly
  // if they've already paid (this is the safety net for the "I paid but landed
  // here anyway" case).  If yes — auto-unlock + go to dashboard.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/payment/recover");
        if (cancelled) return;
        if (data?.has_paid) {
          await refresh();
          navigate("/dashboard", { replace: true });
          return;
        }
      } catch {
        // ignore — show the paywall as usual
      } finally {
        if (!cancelled) setAutoRecovering(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.get("/payment/mode")
      .then((r) => setTestMode(!!r.data.test_mode))
      .catch(() => setTestMode(false));
  }, []);

  if (user?.has_paid) {
    navigate("/dashboard", { replace: true });
    return null;
  }

  if (autoRecovering) {
    return (
      <div className="min-h-[calc(100vh-64px)] flex items-center justify-center bg-[#F4F4F4]" data-testid="paywall-autorecover">
        <div className="font-mono-civic text-sm uppercase tracking-widest text-black">{t("paysucc_recovering")}</div>
      </div>
    );
  }

  const handlePay = async () => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/payment/checkout", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (err) {
      setError(formatApiError(err));
      setLoading(false);
    }
  };

  const handlePreviewUnlock = async () => {
    setError(""); setUnlocking(true);
    try {
      await api.post("/payment/dev-unlock");
      await refresh();
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
      setUnlocking(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-64px)] bg-[#F4F4F4] text-black px-5 py-10" data-testid="paywall-page">
      <div className="max-w-3xl mx-auto">
        <div className="font-mono-civic text-xs uppercase tracking-[0.3em] text-black">{t("pay_step")}</div>
        <h1 className="mt-2 font-serif-display font-extrabold text-5xl sm:text-6xl text-black leading-[0.9]">
          {t("pay_h1_a")}<br />{t("pay_h1_b")} {t("pay_h1_loonie")}<br />{t("pay_h1_c")}
        </h1>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="md:col-span-3 bg-white border-2 border-black brutal-shadow p-8">
            <div className="font-mono-civic text-xs uppercase tracking-widest text-black">{t("pay_what")}</div>
            <ul className="mt-5 space-y-3 font-bold text-black">
              <li className="flex gap-3"><span className="text-[#E00000]">▸</span> {t("pay_b1")}</li>
              <li className="flex gap-3"><span className="text-[#E00000]">▸</span> {t("pay_b2")}</li>
              <li className="flex gap-3"><span className="text-[#E00000]">▸</span> {t("pay_b3")}</li>
              <li className="flex gap-3"><span className="text-[#E00000]">▸</span> {t("pay_b4")}</li>
              <li className="flex gap-3"><span className="text-[#E00000]">▸</span> {t("pay_b5")}</li>
            </ul>
          </div>

          <div className="md:col-span-2 bg-black text-white border-2 border-black p-8 flex flex-col">
            <div className="font-mono-civic text-xs uppercase tracking-widest text-white/70">{t("pay_total_due")}</div>
            <div className="mt-2 font-serif-display font-extrabold text-7xl leading-none">
              $2<span className="text-3xl align-top">.49</span>
            </div>
            <div className="font-mono-civic text-xs uppercase tracking-widest text-white/70 mt-1">{t("pay_one_time")}</div>

            {testMode && (
              <div className="mt-5 border-2 border-white/40 bg-white/5 p-3 text-left">
                <div className="font-mono-civic text-[10px] uppercase tracking-widest text-white/80" style={{ fontWeight: 800 }}>
                  {t("pay_test_mode")}
                </div>
                <p className="mt-1 text-white text-xs sm:text-sm" style={{ fontWeight: 700 }}>
                  {t("pay_test_intro")}
                </p>
                <ul className="mt-2 text-white text-xs font-mono-civic space-y-0.5">
                  <li>{t("pay_test_card")} <span style={{ fontWeight: 900 }}>4242 4242 4242 4242</span></li>
                  <li>{t("pay_test_exp")}</li>
                  <li>{t("pay_test_zip")}</li>
                </ul>
                <p className="mt-2 text-white/70 text-[10px]" style={{ fontWeight: 700 }}>
                  {t("pay_test_note")}
                </p>
              </div>
            )}

            {error && (
              <div className="mt-5 border-2 border-[#E00000] bg-white p-3 text-[#E00000] text-sm" style={{ fontWeight: 800 }} data-testid="paywall-error">
                {error}
              </div>
            )}

            <button onClick={handlePay} disabled={loading || unlocking}
              className="mt-5 bg-[#E00000] text-white uppercase tracking-widest py-5 border-2 border-white hover:bg-white hover:text-black hover:border-black transition-colors disabled:opacity-60"
              style={{ fontWeight: 900 }}
              data-testid="paywall-pay-btn">
              {loading ? t("pay_redirecting") : t("pay_button")}
            </button>
            <p className="mt-3 font-mono-civic text-[10px] uppercase tracking-widest text-white/60">
              {t("pay_stripe_note")}
            </p>

            {testMode && (
              <>
                <div className="mt-5 flex items-center gap-3 text-white/60 font-mono-civic text-[10px] uppercase tracking-widest">
                  <span className="flex-1 h-[1px] bg-white/30" />
                  <span>{t("pay_or")}</span>
                  <span className="flex-1 h-[1px] bg-white/30" />
                </div>
                <button onClick={handlePreviewUnlock} disabled={unlocking || loading}
                  className="mt-3 bg-white text-black uppercase tracking-widest py-4 border-2 border-white hover:bg-black hover:text-white transition-colors disabled:opacity-60"
                  style={{ fontWeight: 900 }}
                  data-testid="paywall-preview-btn">
                  {unlocking ? t("pay_preview_loading") : t("pay_preview_btn")}
                </button>
                <p className="mt-2 font-mono-civic text-[10px] uppercase tracking-widest text-white/60">
                  {t("pay_preview_disclaimer")}
                </p>
              </>
            )}
          </div>
        </div>

        {/* Trust panel — addresses the #1 conversion objection for new sites:
            "is putting my credit card here safe?"  This is rendered after the
            checkout card so it's the LAST thing the visitor sees before
            clicking 'Pay'. */}
        <div className="mt-8">
          <TrustBadges />
        </div>
      </div>
    </div>
  );
}
